import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus } from 'lucide-react';
import type { Page, Component } from '../ComponentEditModal.types';
import { ImageGalleryItemCard, type GalleryItem } from './ImageGalleryItemCard';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';
import { ButtonsEditor } from '../shared/ButtonsEditor';
import type { ButtonData } from '../shared/ButtonsEditor';
import { NumberInput } from '../shared/NumberInput';

interface ImageGalleryData {
  showTitle?: boolean;
  title?: string;
  showSubtitle?: boolean;
  subtitle?: string;
  layout?: string;
  columns?: number;
  spacing?: string;
  aspectRatio?: string;
  captionPosition?: string;
  lightbox?: boolean;
  lazyLoading?: boolean;
  filtering?: boolean;
  items?: GalleryItem[];
  customColors?: {
    background?: string;
    title?: string;
    caption?: string;
    filterButtonActive?: string;
  };
  backgroundColor?: string;
  titleColor?: string;
  captionColor?: string;
  buttons?: ButtonData[];
}

interface ImageGalleryEditorProps {
  formData: ImageGalleryData;
  setFormData: (data: ImageGalleryData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  onOpenMediaUpload: (mediaType: 'image' | 'video', assetType: string, itemIndex?: number) => void;
  currentPageComponents?: Component[];
}

export const ImageGalleryEditor: React.FC<ImageGalleryEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  onOpenMediaUpload,
  currentPageComponents,
}) => {
  const data = formData || {};
  const items = data.items || [];
  const buttons = data.buttons || [];
  const themeColors = useCustomColors();

  const addGalleryItem = () => {
    const newItem: GalleryItem = {
      id: uuidv4(),
      imageUrl: '',
      cloudfrontUrl: '',
      alt: '',
      caption: '',
      category: '',
      order: items.length,
    };
    setFormData({ ...data, items: [...items, newItem] });
  };

  const updateGalleryItem = (index: number, field: string, value: any) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setFormData({ ...data, items: updatedItems });
  };

  const updateGalleryItemFields = (index: number, fields: Record<string, any>) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], ...fields };
    setFormData({ ...data, items: updatedItems });
  };

  const removeGalleryItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  const moveGalleryItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === items.length - 1)
    ) {
      return;
    }
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updatedItems = [...items];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    updatedItems.forEach((item, i) => {
      item.order = i;
    });
    setFormData({ ...data, items: updatedItems });
  };

  // Get unique categories from items
  const existingCategories: string[] = Array.from(
    new Set(items.map(item => item.category).filter((cat): cat is string => Boolean(cat)))
  );

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('image-gallery', themeColors);

  return (
    <div className="space-y-6">
      {/* Title and Subtitle */}
      <HeadlinesSection data={data} setFormData={setFormData} />

      {/* Layout Options */}
      <LayoutSection data={data} setFormData={setFormData} />

      {/* Toggle Options */}
      <FeaturesSection data={data} setFormData={setFormData} />

      {/* Categories Management */}
      {data.filtering && (
        <CategoriesSection items={items} existingCategories={existingCategories} />
      )}

      {/* Gallery Items */}
      <div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Images ({items.length})</label>
        </div>

        <div className="space-y-4">
          {items.map((item, index) => (
            <ImageGalleryItemCard
              key={item.id}
              item={item}
              index={index}
              totalItems={items.length}
              filtering={data.filtering || false}
              aspectRatio={data.aspectRatio}
              onUpdate={(field, value) => updateGalleryItem(index, field, value)}
              onUpdateFields={fields => updateGalleryItemFields(index, fields)}
              onRemove={() => removeGalleryItem(index)}
              onMove={direction => moveGalleryItem(index, direction)}
              onUpload={() => onOpenMediaUpload('image', 'gallery', index)}
            />
          ))}
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={addGalleryItem}
            className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
          >
            <Plus size={16} />
            <span>Add Image</span>
          </button>
        </div>
      </div>

      {/* Call-to-Action Buttons */}
      <ButtonsEditor
        buttons={buttons}
        onChange={newButtons => setFormData({ ...data, buttons: newButtons })}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        label="Call-to-Action Buttons (Optional)"
      />

      {/* Custom Colors */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize gallery colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | null | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
      />
    </div>
  );
};

// Sub-components

interface HeadlinesSectionProps {
  data: ImageGalleryData;
  setFormData: (data: ImageGalleryData) => void;
}

const HeadlinesSection: React.FC<HeadlinesSectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Headlines</h3>
    <div className="space-y-4">
      <div>
        <label className="flex items-center space-x-2 mb-2">
          <input
            type="checkbox"
            checked={data.showTitle !== false}
            onChange={e => setFormData({ ...data, showTitle: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Title</span>
        </label>
        {data.showTitle !== false && (
          <input
            type="text"
            value={data.title || ''}
            onChange={e => setFormData({ ...data, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 mt-2"
            placeholder="Image Gallery"
          />
        )}
      </div>
      <div>
        <label className="flex items-center space-x-2 mb-2">
          <input
            type="checkbox"
            checked={data.showSubtitle !== false}
            onChange={e => setFormData({ ...data, showSubtitle: e.target.checked })}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded-md focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Subtitle</span>
        </label>
        {data.showSubtitle !== false && (
          <input
            type="text"
            value={data.subtitle || ''}
            onChange={e => setFormData({ ...data, subtitle: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 mt-2"
            placeholder="Browse through our collection"
          />
        )}
      </div>
    </div>
  </div>
);

interface LayoutSectionProps {
  data: ImageGalleryData;
  setFormData: (data: ImageGalleryData) => void;
}

const LayoutSection: React.FC<LayoutSectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Layout & Display</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout Style</label>
        <select
          value={data.layout || 'grid'}
          onChange={e => setFormData({ ...data, layout: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="grid">Grid</option>
          <option value="masonry">Masonry</option>
          <option value="carousel">Carousel</option>
          <option value="divider">Divider</option>
          <option value="slideshow">Slideshow</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Columns (1-6)</label>
        <NumberInput
          min={1}
          max={6}
          value={data.columns || 3}
          fallback={3}
          onChange={columns => setFormData({ ...data, columns })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Spacing</label>
        <select
          value={data.spacing || 'normal'}
          onChange={e => setFormData({ ...data, spacing: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="compact">Compact</option>
          <option value="normal">Normal</option>
          <option value="spacious">Spacious</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Aspect Ratio</label>
        <select
          value={data.aspectRatio || 'auto'}
          onChange={e => setFormData({ ...data, aspectRatio: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="auto">Auto</option>
          <option value="square">Square</option>
          <option value="16:9">16:9</option>
          <option value="4:3">4:3</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Caption Position</label>
        <select
          value={data.captionPosition || 'below'}
          onChange={e => setFormData({ ...data, captionPosition: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="below">Below Image</option>
          <option value="overlay">Overlay</option>
          <option value="none">None</option>
        </select>
      </div>
    </div>
  </div>
);

interface FeaturesSectionProps {
  data: ImageGalleryData;
  setFormData: (data: ImageGalleryData) => void;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({ data, setFormData }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.lightbox !== false}
          onChange={e => setFormData({ ...data, lightbox: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Enable Lightbox</span>
      </label>
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.lazyLoading !== false}
          onChange={e => setFormData({ ...data, lazyLoading: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Lazy Loading</span>
      </label>
      <label className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={data.filtering || false}
          onChange={e => setFormData({ ...data, filtering: e.target.checked })}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700">Enable Filtering</span>
      </label>
    </div>
  </div>
);

interface CategoriesSectionProps {
  items: GalleryItem[];
  existingCategories: string[];
}

const CategoriesSection: React.FC<CategoriesSectionProps> = ({ items, existingCategories }) => (
  <div className="border-b pb-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
    <div className="space-y-2">
      {existingCategories.map((category, index) => (
        <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-2">
          <input
            type="text"
            value={category}
            readOnly
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
          />
          <span className="text-sm text-gray-500 whitespace-nowrap">
            (Used by {items.filter(item => item.category === category).length} image(s))
          </span>
        </div>
      ))}
      <p className="text-sm text-gray-500 mt-2">
        Categories are automatically created from image category fields.
      </p>
    </div>
  </div>
);
